import { useState, useEffect } from 'react';
import { Settings2 } from 'lucide-react';
import { usePlatformSetting, useUpdatePlatformSetting } from '@/hooks/usePlatformSettings';
import { useAdminModels } from '@/hooks/useAdminModels';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const PlatformSettingsPanel = () => {
  const { data: models = [], isLoading: modelsLoading } = useAdminModels();
  const { data: currentTitleModel, isLoading: settingLoading } = usePlatformSetting<string>('title_model');
  const updateSetting = useUpdatePlatformSetting();
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (currentTitleModel) setSelected(currentTitleModel);
  }, [currentTitleModel]);

  const isLoading = modelsLoading || settingLoading;
  const hasChanges = selected && selected !== currentTitleModel;

  const handleSave = () => {
    if (!selected) return;
    updateSetting.mutate({ key: 'title_model', value: selected });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-full max-w-md" />
      </div>
    );
  }

  // Sort: active first, then by display_name
  const sortedModels = [...models].sort((a: any, b: any) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return (a.display_name || '').localeCompare(b.display_name || '');
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Settings2 className="h-4 w-4 text-brand-primary" />
        <h3 className="text-sm font-semibold text-primary">Configurações da Plataforma</h3>
      </div>

      <div className="space-y-2 max-w-md">
        <label className="text-sm font-medium text-primary">
          Modelo para gerar títulos de conversas
        </label>
        <p className="text-xs text-secondary">
          Modelo usado automaticamente para gerar nomes curtos de novas conversas. Recomendado: modelo rápido e barato.
        </p>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione um modelo..." />
          </SelectTrigger>
          <SelectContent>
            {sortedModels.map((m: any) => (
              <SelectItem key={m.id} value={m.id}>
                <span className={!m.is_active ? 'opacity-50' : ''}>
                  {m.display_name} {!m.is_active && '(inativo)'}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasChanges && (
        <Button size="sm" onClick={handleSave} disabled={updateSetting.isPending}>
          {updateSetting.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      )}
    </div>
  );
};
